/* screens/ItineraryForm.jsx */
import "./Profile.css";
import BackBtn from "./Shared/BackBtn";
import { TextField, IconButton, CircularProgress } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate, useParams } from "react-router-dom";
import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import backend from "../../../../../../../api";
import { setUserData } from "../../../../../../../redux-store/auth/actions";
import { useSnackbar } from "notistack";
import { motion, easeOut } from "framer-motion";

export default function ItineraryForm(){
  const { id } = useParams();          // undefined => add, else edit
  const { user } = useSelector(s=>s.auth);
  const dispatch = useDispatch();
  const nav = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const original = id ? (user?.itineraries?.[id]||{}) : {};
  const [from,setFrom] = useState(original.from?.slice(0,10)||"");
  const [to  ,setTo]   = useState(original.to  ?.slice(0,10)||"");
  const [days,setDays] = useState(original.days?.length ? original.days : [{date:"",activities:[""]}]);
  const [saving,setSaving]=useState(false);

  const addDay=()=> setDays([...days,{date:"",activities:[""]}]);
  const addAct=(d)=> setDays(days.map((day,i)=>i===d?{...day,activities:[...day.activities,""]}:day));
  const delDay=i=> setDays(days.filter((_,idx)=>idx!==i));

  const onDayDate=(i,v)=> setDays(days.map((d,idx)=>idx===i?{...d,date:v}:d));
  const onAct=(di,ai,v)=> setDays(days.map((d,idx)=>idx===di?{
    ...d,
    activities: d.activities.map((a,j)=>j===ai?v:a)
  }:d));

  // NEW: delete activity row (ensures at least one field remains)
  const delAct = (di, ai) => {
    setDays(days.map((d, idx) => {
      if (idx !== di) return d;
      const next = d.activities.filter((_, j) => j !== ai);
      return { ...d, activities: next.length ? next : [""] };
    }));
  };

  // Helpers
  const isBlank = (s) => !s || !String(s).trim();

  const cleanDays = (arr) => {
    // trim and drop empty activities; drop days with no date or no activities
    const cleaned = arr.map(d => ({
      date: (d.date || "").slice(0,10),
      activities: (d.activities || []).map(a => (a ?? "")?.trim()).filter(a => a.length > 0)
    })).filter(d => d.date && d.activities.length > 0);

    return cleaned;
  };

  const save=async()=>{
    // === VALIDATIONS (snackbar) ===
    if (isBlank(from) || isBlank(to)) {
      enqueueSnackbar('Please select both "From" and "To" dates.', { variant: "warning" });
      return;
    }
    if (from > to) {
      enqueueSnackbar('Start date ("From") cannot be after end date ("To").', { variant: "warning" });
      return;
    }

    const cleanedDays = cleanDays(days);

    if (cleanedDays.length === 0) {
      enqueueSnackbar('Add at least one day with a date and one non-empty activity.', { variant: "warning" });
      return;
    }

    const outOfRange = cleanedDays.some(d => d.date < from || d.date > to);
    if (outOfRange) {
      enqueueSnackbar('Each Day date must lie within the selected From–To range.', { variant: "warning" });
      return;
    }

    setSaving(true);
    try{
      const newItin={ from, to, days: cleanedDays };
      const arr=[...(user?.itineraries||[])];
      if(id!==undefined) arr[+id]=newItin; else arr.push(newItin);

      const {data}=await backend.put(`/api/user/${user._id}`,{itineraries:arr});
      dispatch(setUserData({...user,...data.data}));
      enqueueSnackbar("Saved!",{variant:"success"});
      nav(-1);
    }catch(e){
      enqueueSnackbar(e?.response?.data?.message || "Error", { variant:"error" });
    }finally{
      setSaving(false);
    }
  };

  return(
    <motion.div className="pf-wrap" initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, ease: easeOut }}>
      <BackBtn/>
      <h2 className="pf-modal-title" style={{textAlign:"left"}}>{id!==undefined?"Edit":"Add"} Travel itineraries</h2>

      {/* dates */}
      <div className="pf-it-form">
        <TextField
          fullWidth type="date" size="small" label="From" InputLabelProps={{shrink:true}}
          value={from} onChange={e=>setFrom(e.target.value)} />
        <TextField sx={{mt:2}}
          fullWidth type="date" size="small" label="To" InputLabelProps={{shrink:true}}
          value={to} onChange={e=>setTo(e.target.value)} />

        {/* days */}
        {days.map((d,di)=>(
          <div key={di} style={{marginTop:28,padding:18,border:"1px solid #eee",borderRadius:16}}>
            <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:12}}>
              <TextField type="date" size="small" label={`Day ${di+1} date`}
                InputLabelProps={{shrink:true}} value={d.date}
                onChange={e=>onDayDate(di,e.target.value)} />
              {days.length>1 && (
                <IconButton onClick={()=>delDay(di)} aria-label="Delete day"><DeleteIcon fontSize="16"/></IconButton>
              )}
            </div>

            {d.activities.map((a,ai)=>(
              <div key={ai} style={{display:"flex",alignItems:"center",gap:8, marginBottom:8}}>
                <TextField
                  size="small"
                  fullWidth
                  label={`Activity ${ai+1}`}
                  value={a}
                  onChange={e=>onAct(di,ai,e.target.value)}
                />
                {d.activities.length > 1 && (
                  <IconButton
                    aria-label={`Delete activity ${ai+1}`}
                    onClick={()=>delAct(di, ai)}
                    size="small"
                  >
                    <DeleteIcon fontSize="16" />
                  </IconButton>
                )}
              </div>
            ))}
            <button className="pf-add-mini" onClick={()=>addAct(di)}>
              <AddIcon fontSize="14"/> Add
            </button>
          </div>
        ))}
        <button className="pf-add-mini" onClick={addDay}><AddIcon fontSize="14"/> Add Day</button>

        <button className="pf-save" onClick={save} disabled={saving}>
          {saving ? <CircularProgress size={20} sx={{color:"#fff"}}/> : "Save"}
        </button>
      </div>
    </motion.div>
  );
}
